import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from '@/components/i18n/Translator';
import { GitHub } from '@/components/icons/Github';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

const GITHUB_URL_PATTERN = /^https?:\/\/(www\.)?github\.com\/[\w.-]+\/[\w.-]+/;

interface GithubRepo {
  full_name: string;
  name: string;
  private: boolean;
  description: string;
  html_url: string;
  default_branch: string;
}

interface GithubStatus {
  configured: boolean;
  connected: boolean;
  login?: string | null;
  reason?: string | null;
  scopes?: string | null;
  /** False when the server only granted read-only public scopes. */
  private_access?: boolean;
}

interface GithubPublicUser {
  login: string;
  name: string;
  avatar_url: string;
  html_url: string;
  public_repos: number;
}

const GITHUB_USERNAME_PATTERN = /^[A-Za-z0-9](?:[A-Za-z0-9]|-(?=[A-Za-z0-9])){0,38}$/;

interface Props {
  disabled?: boolean;
  onRepoSelected: (repoUrl: string) => void;
  onRepoError: (error: string) => void;
  /** Hide the toolbar icon — open via `open` / `onOpenChange` (e.g. from + menu). */
  hideTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const GitHubPickerButton = ({
  disabled = false,
  onRepoSelected,
  onRepoError,
  hideTrigger = false,
  open: openProp,
  onOpenChange,
}: Props) => {
  const { i18n } = useTranslation();
  const en = i18n.language.toLowerCase().startsWith('en');
  const copy = useMemo(
    () =>
      en
        ? {
            title: 'Add a GitHub repository',
            description:
              'Connect GitHub to access your repositories, including private ones, or paste a public repository URL.',
            checking: 'Checking connection…',
            connectedAs: 'Connected as',
            disconnect: 'Disconnect',
            oauthMissing:
              'Private repository access is not configured on this server. You can still paste a public repository URL.',
            connectHint: 'Connect your account to access private repositories.',
            connect: 'Connect GitHub',
            yourRepos: 'Your repositories',
            loading: 'Loading…',
            noRepos: 'No repositories available.',
            privateRepo: 'private',
            byUsername: 'Find by GitHub username',
            usernamePlaceholder: 'e.g. octocat',
            search: 'Search',
            publicReposOf: 'Public repositories of',
            noPublicRepos: 'This account has no public repositories.',
            invalidUsername: 'Enter a valid GitHub username (letters, digits, hyphens).',
            userNotFound: 'That GitHub user does not exist.',
            searchFailed: 'Could not search GitHub. Try again.',
            publicOnly: 'Connected for public repositories only.',
            pasteUrl: 'Or paste a URL',
            checkingRepo: 'Checking repository…',
            validRepo: 'Repository is valid.',
            cancel: 'Cancel',
            add: 'Add',
            invalidUrl:
              'Enter a valid GitHub URL (for example, https://github.com/user/repo).',
            unavailable: 'Repository was not found or is not accessible.',
            validationFailed: 'Could not check the repository. Try again.',
          }
        : {
            title: 'Dodaj GitHub repozitorijum',
            description:
              'Poveži GitHub da pristupiš svojim repozitorijumima, uključujući privatne, ili nalepi URL javnog repozitorijuma.',
            checking: 'Proveravam vezu…',
            connectedAs: 'GitHub nalog',
            disconnect: 'Prekini vezu',
            oauthMissing:
              'Pristup privatnim repozitorijumima nije konfigurisan na serveru. I dalje možeš da nalepiš URL javnog repozitorijuma.',
            connectHint: 'Poveži nalog da pristupiš privatnim repozitorijumima.',
            connect: 'Poveži GitHub',
            yourRepos: 'Tvoji repozitorijumi',
            loading: 'Učitavam…',
            noRepos: 'Nema dostupnih repozitorijuma.',
            privateRepo: 'privatan',
            byUsername: 'Pronađi preko GitHub korisničkog imena',
            usernamePlaceholder: 'npr. octocat',
            search: 'Traži',
            publicReposOf: 'Javni repozitorijumi korisnika',
            noPublicRepos: 'Ovaj nalog nema javnih repozitorijuma.',
            invalidUsername:
              'Unesi važeće GitHub korisničko ime (slova, cifre, crtice).',
            userNotFound: 'Taj GitHub korisnik ne postoji.',
            searchFailed: 'Ne mogu da pretražim GitHub. Pokušaj ponovo.',
            publicOnly: 'Povezano samo za javne repozitorijume.',
            pasteUrl: 'Ili nalepi URL',
            checkingRepo: 'Proveravam repozitorijum…',
            validRepo: 'Repozitorijum je validan.',
            cancel: 'Otkaži',
            add: 'Dodaj',
            invalidUrl:
              'Unesi validan GitHub URL (na primer, https://github.com/user/repo).',
            unavailable: 'Repozitorijum nije pronađen ili nije dostupan.',
            validationFailed: 'Ne mogu da proverim repozitorijum. Pokušaj ponovo.',
          },
    [en],
  );
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = openProp ?? uncontrolledOpen;
  const setIsOpen = useCallback(
    (next: boolean) => {
      if (openProp === undefined) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [openProp, onOpenChange],
  );
  const [repoUrl, setRepoUrl] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [status, setStatus] = useState<GithubStatus | null>(null);
  const [repos, setRepos] = useState<GithubRepo[]>([]);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [username, setUsername] = useState('');
  const [publicUser, setPublicUser] = useState<GithubPublicUser | null>(null);
  const [publicRepos, setPublicRepos] = useState<GithubRepo[]>([]);
  const [searchingUser, setSearchingUser] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const searchIdRef = useRef(0);

  const resetDialog = useCallback(() => {
    setRepoUrl('');
    setIsValid(false);
    setIsValidating(false);
    setErrorMsg('');
    setUsernameError('');
    setPublicUser(null);
    setPublicRepos([]);
    setSearchingUser(false);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const response = await fetch('/api/github/status', {
        credentials: 'same-origin',
      });
      if (!response.ok) {
        setStatus({ configured: false, connected: false });
        return;
      }
      const data = (await response.json()) as GithubStatus;
      setStatus(data);
      return data;
    } catch {
      setStatus({ configured: false, connected: false });
      return null;
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  const loadRepos = useCallback(async () => {
    setLoadingRepos(true);
    try {
      const response = await fetch('/api/github/repos', {
        credentials: 'same-origin',
      });
      if (!response.ok) {
        setRepos([]);
        return;
      }
      const data = await response.json();
      setRepos(Array.isArray(data?.repos) ? data.repos : []);
    } catch {
      setRepos([]);
    } finally {
      setLoadingRepos(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    void (async () => {
      const s = await loadStatus();
      if (s?.connected) {
        await loadRepos();
        // Their own handle is the likeliest search — fill it in, don't run it.
        if (s.login) setUsername((current) => current || s.login || '');
      } else {
        setRepos([]);
      }
    })();
  }, [isOpen, loadStatus, loadRepos]);

  const searchByUsername = useCallback(
    async (value: string) => {
      const handle = value.trim().replace(/^@/, '');
      if (!handle) return;
      if (!GITHUB_USERNAME_PATTERN.test(handle)) {
        setUsernameError(copy.invalidUsername);
        setPublicUser(null);
        setPublicRepos([]);
        return;
      }

      const searchId = ++searchIdRef.current;
      setSearchingUser(true);
      setUsernameError('');
      try {
        const response = await fetch(
          `/api/github/users/${encodeURIComponent(handle)}/repos`,
          { credentials: 'same-origin' },
        );
        if (searchId !== searchIdRef.current) return;

        if (!response.ok) {
          setPublicUser(null);
          setPublicRepos([]);
          setUsernameError(
            response.status === 404 ? copy.userNotFound : copy.searchFailed,
          );
          return;
        }
        const data = await response.json();
        if (searchId !== searchIdRef.current) return;
        setPublicUser((data?.user as GithubPublicUser) ?? null);
        setPublicRepos(Array.isArray(data?.repos) ? data.repos : []);
      } catch {
        if (searchId !== searchIdRef.current) return;
        setPublicUser(null);
        setPublicRepos([]);
        setUsernameError(copy.searchFailed);
      } finally {
        if (searchId === searchIdRef.current) setSearchingUser(false);
      }
    },
    [copy],
  );

  const validateUrl = useCallback(
    async (value: string) => {
      const trimmed = value.trim();
      if (trimmed.length < 5) {
        setIsValid(false);
        setErrorMsg('');
        return;
      }

      if (!GITHUB_URL_PATTERN.test(trimmed)) {
        setIsValid(false);
        setErrorMsg(copy.invalidUrl);
        return;
      }

      const requestId = ++requestIdRef.current;
      setIsValidating(true);
      setErrorMsg('');
      try {
        const response = await fetch('/api/github/repo/validate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ url: trimmed }),
        });

        if (requestId !== requestIdRef.current) return;

        if (response.ok) {
          setIsValid(true);
          setErrorMsg('');
        } else {
          setIsValid(false);
          let detail = copy.unavailable;
          try {
            const data = await response.json();
            if (typeof data?.detail === 'string') detail = data.detail;
          } catch {
            /* ignore */
          }
          setErrorMsg(detail);
          onRepoError(detail);
        }
      } catch {
        if (requestId !== requestIdRef.current) return;
        setIsValid(false);
        const detail = copy.validationFailed;
        setErrorMsg(detail);
        onRepoError(detail);
      } finally {
        if (requestId === requestIdRef.current) {
          setIsValidating(false);
        }
      }
    },
    [copy, onRepoError],
  );

  const handleUrlChange = useCallback(
    (value: string) => {
      setRepoUrl(value);
      setIsValid(false);
      setErrorMsg('');
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        void validateUrl(value);
      }, 400);
    },
    [validateUrl],
  );

  const handleSubmit = useCallback(() => {
    if (!isValid) return;
    onRepoSelected(repoUrl.trim());
    setIsOpen(false);
    resetDialog();
  }, [isValid, onRepoSelected, repoUrl, resetDialog]);

  const handlePickRepo = useCallback(
    (repo: GithubRepo) => {
      onRepoSelected(repo.html_url);
      setIsOpen(false);
      resetDialog();
    },
    [onRepoSelected, resetDialog],
  );

  const handleDisconnect = useCallback(async () => {
    setDisconnecting(true);
    try {
      await fetch('/api/github/disconnect', {
        method: 'POST',
        credentials: 'same-origin',
      });
      setStatus({ configured: status?.configured ?? true, connected: false });
      setRepos([]);
    } catch (e) {
      onRepoError(String(e));
    } finally {
      setDisconnecting(false);
    }
  }, [onRepoError, status?.configured]);

  return (
    <>
      {!hideTrigger && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-block">
                <Button
                  id="github-button"
                  variant="ghost"
                  size="icon"
                  className="text-secondary"
                  disabled={disabled}
                  onClick={() => setIsOpen(true)}
                >
                  <GitHub />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{copy.title}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (!open) resetDialog();
        }}
      >
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{copy.title}</DialogTitle>
            <DialogDescription>{copy.description}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {loadingStatus ? (
              <p className="text-sm text-muted-foreground">{copy.checking}</p>
            ) : status?.connected ? (
              <div className="flex items-center justify-between gap-2 rounded-md border px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm">
                    {copy.connectedAs}{' '}
                    <span className="font-medium">{status.login}</span>
                  </p>
                  {status.private_access === false && (
                    <p className="text-xs text-muted-foreground">
                      {copy.publicOnly}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={disconnecting}
                  onClick={() => void handleDisconnect()}
                >
                  {disconnecting ? '…' : copy.disconnect}
                </Button>
              </div>
            ) : (
              <div className="space-y-2 rounded-md border px-3 py-3">
                <p className="text-sm text-muted-foreground">
                  {status?.configured === false
                    ? copy.oauthMissing
                    : copy.connectHint}
                </p>
                {status?.configured !== false && (
                  <Button asChild className="w-full">
                    <a href="/api/github/oauth/start">{copy.connect}</a>
                  </Button>
                )}
              </div>
            )}

            <div className="space-y-2">
              <p className="text-sm font-medium">{copy.byUsername}</p>
              <div className="flex gap-2">
                <Input
                  value={username}
                  placeholder={copy.usernamePlaceholder}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setUsernameError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void searchByUsername(username);
                    }
                  }}
                />
                <Button
                  variant="outline"
                  disabled={searchingUser || !username.trim()}
                  onClick={() => void searchByUsername(username)}
                >
                  {searchingUser ? '…' : copy.search}
                </Button>
              </div>
              {usernameError && (
                <p className="text-sm text-destructive">{usernameError}</p>
              )}
              {publicUser && !usernameError && (
                <>
                  <p className="text-xs text-muted-foreground">
                    {copy.publicReposOf}{' '}
                    <span className="font-medium">{publicUser.login}</span>
                    {publicUser.name ? ` (${publicUser.name})` : ''}
                  </p>
                  {publicRepos.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {copy.noPublicRepos}
                    </p>
                  ) : (
                    <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-1">
                      {publicRepos.map((repo) => (
                        <li key={`public-${repo.full_name}`}>
                          <button
                            type="button"
                            className="fongpt-hover flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm text-secondary"
                            onClick={() => handlePickRepo(repo)}
                          >
                            <span className="truncate font-medium">
                              {repo.name}
                            </span>
                            {repo.description && (
                              <span className="hidden shrink-0 max-w-[45%] truncate text-xs text-muted-foreground sm:block">
                                {repo.description}
                              </span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>

            {status?.connected && (
              <div className="space-y-2">
                <p className="text-sm font-medium">{copy.yourRepos}</p>
                {loadingRepos ? (
                  <p className="text-sm text-muted-foreground">{copy.loading}</p>
                ) : repos.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {copy.noRepos}
                  </p>
                ) : (
                  <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-1">
                    {repos.map((repo) => (
                      <li key={repo.full_name}>
                        <button
                          type="button"
                          className="fongpt-hover flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm text-secondary"
                          onClick={() => handlePickRepo(repo)}
                        >
                          <span className="truncate font-medium">
                            {repo.full_name}
                          </span>
                          {repo.private && (
                            <span className="shrink-0 text-xs text-muted-foreground">
                              {copy.privateRepo}
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="space-y-2">
              <p className="text-sm font-medium">{copy.pasteUrl}</p>
              <Input
                type="url"
                name="github-repository-url"
                inputMode="url"
                autoComplete="url"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                data-1p-ignore="true"
                data-lpignore="true"
                data-form-type="other"
                placeholder="https://github.com/username/repo"
                value={repoUrl}
                onChange={(e) => handleUrlChange(e.target.value)}
                disabled={isValidating}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && isValid && !isValidating) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              />
              {isValidating && (
                <p className="text-sm text-muted-foreground">
                  {copy.checkingRepo}
                </p>
              )}
              {isValid && !isValidating && (
                <p className="text-sm text-muted-foreground">
                  {copy.validRepo}
                </p>
              )}
              {errorMsg && !isValidating && (
                <p className="text-sm text-destructive">{errorMsg}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsOpen(false);
                resetDialog();
              }}
            >
              {copy.cancel}
            </Button>
            <Button onClick={handleSubmit} disabled={!isValid || isValidating}>
              {copy.add}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GitHubPickerButton;
